import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import Dropzone from 'react-dropzone';
import Overlay from '../../shared/Overlay';
import { Helmet } from 'react-helmet-async';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';
import { cancelText } from '../../shared/SharedInternationalizations';
import { Link } from 'react-router-dom';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../../shared/Button';
import SvgUploadIcon from '../../svg/UploadIcon';
import SVGCheckMarkIcon from '../../svg/CheckMarkIcon';
import { importUsers } from '../../../redux/actions/userActions';
import UserCsvTemplate from '../../../assets/user_import_template.csv?url';

export function UnconnectedUsersImport(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state = {} } = location;
  const previous = state.previous || '/users/';
  const [data, setData] = useState(null);

  const loading = props?.ui?.loading

  function handleFileSelect(params) {
    setData(params);
  }

  function handleSubmit() {
    props.dispatch(importUsers(data, navigate, previous));
  }

  const linkRef = useRef();

  function csvDownload() {
    linkRef.current.click();
  }

  return (
    <Overlay
      rootUrl={previous}
      canClose={true}
      data-test="users-import-overlay">
      <a
        href={UserCsvTemplate}
        id="download"
        ref={linkRef}
        className="visually-hidden"
        download>
        Download
      </a>
      <FormattedMessage
        id="usersImort.browserTabInfo"
        defaultMessage="Users | Import"
        description="Done button">
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="content-container content-container--column">
        <FormattedMessage
          id="usersImport.exportUsersTitle"
          defaultMessage="Import Users"
          description="The title for the page to edit a unit"
          data-test="form-header">
          {(txt) => (
            <h1 className="no-margin text-center" data-test="form-header-text">
              {txt}
            </h1>
          )}
        </FormattedMessage>
        <hr />
        <div className="content-display__container" data-test="user-upload">
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-container">
              <div className="item-container__main-container">
                <div className="item-container__secondary-text">
                  <FormattedMessage
                    id="usersImport.importMainText"
                    defaultMessage="This interface allows for bulk processing and importing of new users into SARA."
                    description="Clears something"
                  />
                  <ul>
                    <FormattedMessage
                      id="usersImport.importInfoText"
                      defaultMessage="<e>When importing users, be sure to have the data saved in a csv file.</e><e>All users submitted should have a first name and a last name.</e><e>If the user is going to log into SARA, they should also have a username entered. </e><e>Users can be created without a username if they are not going to log into SARA. </e><e>If a user has a birthdate listed, please ensure that it is formatted in one of the following formats: 'MM/DD/YY, DD-MM-YYYY, YYYY-MM-DD'. </e><e>For gender,  the options are 'Male' and 'Female' and 'M' and 'F' can be used as well. Capitalizing the gender is not required. </e>"
                      description="Clears something"
                      values={{
                        b: <strong>, c: </strong>,
                        e: (msg) => <li>{msg}</li>,
                      }}
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-pill-container">
              <div className="item-container__main-container">
                <div className="item-container__main-text">
                  <FormattedMessage
                    id="usersImport.sampleFileHeaderText"
                    defaultMessage="Sample CSV File"
                    description="Clears something"
                  />
                </div>
                <div className="item-container__secondary-text">
                  <FormattedMessage
                    id="usersImport.sampleFileText"
                    defaultMessage="To ensure that the imported data will work smoothly in SARA, please use this sample CSV file to input your users for importing."
                    description="Clears something"
                  />
                </div>
              </div>
              <div className="item-container__info-container">
                <Button
                  data-test="download-file-button"
                  buttonType={buttonTypes.secondary}
                  text={
                    <FormattedMessage
                      id="usersImport.downloadFileText"
                      defaultMessage="Download File"
                      description="Opens editing something"
                    />
                  }
                  onClick={csvDownload}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15 js-users-import-sample-file-button"
                />
              </div>
            </div>
          </div>
          <div className="content-display__item-container width-100">
            <Dropzone
              onDrop={(acceptedFiles) => {
                handleFileSelect(acceptedFiles);
              }}>
              {({
                getRootProps,
                getInputProps,
                isDragReject,
                acceptedFiles,
              }) => (
                <div
                  className={`item-container__main-container dropzone-area text-large ${
                    acceptedFiles.length > 0
                      ? 'dropzone-area--item-selected'
                      : ''
                  }`}
                  {...getRootProps()}>
                  <input {...getInputProps()} />
                  {acceptedFiles.length === 0 ? <SvgUploadIcon /> : ''}
                  {!isDragReject && acceptedFiles.length === 0 && (
                    <FormattedMessage
                      id="usersImport.dropzoneUploadText"
                      defaultMessage="Drag a csv file here or click to upload"
                      description="Clears something"
                    />
                  )}
                  {isDragReject && acceptedFiles.length === 0 && (
                    <FormattedMessage
                      id="usersImport.dropzoneFileTypeText"
                      defaultMessage="Sorry, this file type will not work. Please try another type."
                      description="Clears something"
                    />
                  )}

                  {acceptedFiles.length > 0 &&
                    acceptedFiles.map((acceptedFile, index) => (
                      <div className="dropzone-area--selected-item" key={index}>
                        <SVGCheckMarkIcon />
                        <p className="margin-left-10">{acceptedFile.name}</p>
                      </div>
                    ))}

                  {acceptedFiles.length > 0 && (
                    <FormattedMessage
                      id="usersImport.chooseAnotherFileText"
                      defaultMessage="Click again to choose another file or click 'Import Users' below to begin import."
                      description="Clears something"
                    />
                  )}
                </div>
              )}
            </Dropzone>
          </div>
        </div>

        <div className="width-100">
          <p></p>
          <div className="form__section-container form__section-container--spread margin-bottom-25">
            <div className="form__items-group-container" />
            <div className="form__items-group-container">
              <Link
                className="link link-text"
                to={previous}
                data-test="users-upload-form-cancel-link">
                {cancelText}
              </Link>
              <AccessControl
                data-test="access-control"
                feature={permissions.static.users.importUsers}
                permissionMessage={true}>
                <Button
                  buttonType={buttonTypes.success}
                  text={
                    <FormattedMessage
                      id="usersImport.importUsersText"
                      defaultMessage="Import Users"
                      description="Opens editing something"
                    />
                  }
                  onClick={handleSubmit}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15 js-import-submit"
                  disabled={loading}
                  data-test="submit-button"
                />
              </AccessControl>
            </div>
          </div>
        </div>
      </div>
    </Overlay>
  );
}
const mapStateToProps = (state) => {
  return {
    ...state,
  }
}

export default connect(mapStateToProps)(UnconnectedUsersImport);
